import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher/LanguageSwitcher";
import { UserMenu } from "@/components/UserMenu";
import { formatDate } from "@/i18n/format";
import { notificationRoute } from "@/lib/notificationTarget";
import {
  getCommonNotificationsListQueryKey,
  useCommonNotificationsList,
  useCommonNotificationsMarkAllReadCreate,
  useCommonNotificationsMarkReadCreate,
} from "@/services/common/generated/common-notifications/common-notifications";
import type {
  CommonNotificationsList200Item,
  CommonNotificationsList200ItemResultsItem,
} from "@/services/common/generated/model";
import "./DashboardLayout.css";

interface NavItem {
  path: string;
  icon: string;
  labelKey: B2BNavKey;
}

type B2BNavKey =
  | "dashboard"
  | "productService"
  | "requests"
  | "matchResults"
  | "opportunities"
  | "readiness"
  | "dutyEstimate"
  | "trustCompliance"
  | "marketIntelligence"
  | "messages"
  | "contracts"
  | "planUsage"
  | "profile";

interface DashboardLayoutProps {
  children: ReactNode;
  /**
   * @deprecated No-op. The global "Search or type a command" box was removed
   * (WP1.11): it was never wired to anything. Pages search within their own lists.
   */
  showSearch?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    path: "/b2b/dashboard",
    icon: "fa-solid fa-gauge-high",
    labelKey: "dashboard",
  },
  {
    path: "/b2b/product-service",
    icon: "fa-solid fa-cube",
    labelKey: "productService",
  },
  {
    path: "/b2b/requests",
    icon: "fa-solid fa-bullseye",
    labelKey: "requests",
  },
  {
    path: "/b2b/match-results",
    icon: "fa-solid fa-arrow-right-arrow-left",
    labelKey: "matchResults",
  },
  {
    path: "/b2b/opportunities",
    icon: "fa-solid fa-handshake",
    labelKey: "opportunities",
  },
  {
    path: "/b2b/readiness",
    icon: "fa-solid fa-clipboard-check",
    labelKey: "readiness",
  },
  {
    path: "/b2b/trust-compliance",
    icon: "fa-solid fa-shield-halved",
    labelKey: "trustCompliance",
  },
  {
    path: "/b2b/duty-estimate",
    icon: "fa-solid fa-calculator",
    labelKey: "dutyEstimate",
  },
  {
    path: "/b2b/market-intelligence",
    icon: "fa-solid fa-globe",
    labelKey: "marketIntelligence",
  },
  {
    path: "/b2b/messages",
    icon: "fa-regular fa-comment-dots",
    labelKey: "messages",
  },
  {
    path: "/b2b/contracts",
    icon: "fa-regular fa-clock",
    labelKey: "contracts",
  },
  {
    path: "/b2b/plan-usage",
    icon: "fa-solid fa-credit-card",
    labelKey: "planUsage",
  },
  {
    path: "/b2b/profile",
    icon: "fa-regular fa-user",
    labelKey: "profile",
  },
];

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  const { data: notificationsResponse, isLoading: notificationsLoading } = useCommonNotificationsList(
    { page: 1, page_size: 10 },
    { query: { staleTime: 30000 } },
  );

  const markAsReadMutation = useCommonNotificationsMarkReadCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getCommonNotificationsListQueryKey(),
        });
      },
    },
  });

  const markAllReadMutation = useCommonNotificationsMarkAllReadCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getCommonNotificationsListQueryKey(),
        });
      },
    },
  });

  const notificationsPage = notificationsResponse as unknown as CommonNotificationsList200Item | undefined;
  const notifications: CommonNotificationsList200ItemResultsItem[] =
    notificationsPage?.results ??
    (Array.isArray(notificationsResponse)
      ? (notificationsResponse as CommonNotificationsList200ItemResultsItem[])
      : []);
  const unreadCount = notificationsPage?.unreadCount ?? notifications.filter((n) => !n.isRead).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMarkAsRead = (id: number) => {
    markAsReadMutation.mutate({ id });
  };

  const handleOpenNotification = (notification: CommonNotificationsList200ItemResultsItem) => {
    if (!notification.isRead && notification.id != null) {
      handleMarkAsRead(notification.id);
    }
    const route = notificationRoute(notification, "b2b");
    if (route) {
      setShowNotifications(false);
      navigate(route.path, { state: route.state });
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="dashboard">
      {/* Mobile Menu Toggle Button */}
      <button
        type="button"
        className="dashboard__mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label={t("nav.toggleMenu")}
        aria-expanded={isMobileMenuOpen}
      >
        <i className={isMobileMenuOpen ? "fa-solid fa-xmark" : "fa-solid fa-bars"} />
      </button>

      {/* Sidebar */}
      <aside className={`dashboard__sidebar ${isMobileMenuOpen ? "dashboard__sidebar--open" : ""}`}>
        <div className="dashboard__sidebar-header">
          <img className="dashboard__logo-mark" src="/apple-touch-icon.png" alt="" aria-hidden="true" />
          <span className="dashboard__logo-text">Matchpoint Intelligence</span>
        </div>

        <nav className="dashboard__nav" aria-label={t("nav.mainNavigation")}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.path}
              className={`dashboard__nav-item ${location.pathname === item.path ? "dashboard__nav-item--active" : ""}`}
              onClick={() => handleNavClick(item.path)}
              aria-current={location.pathname === item.path ? "page" : undefined}
            >
              <i className={item.icon} />
              <span>{t(`b2bNav.${item.labelKey}`)}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="dashboard__mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
      )}

      {/* Main Content */}
      <main className="dashboard__main">
        <header className="dashboard__topbar">
          <div />

          <div className="dashboard__topbar-actions">
            <div className="dashboard__notifications" ref={notificationRef}>
              <button
                type="button"
                className="dashboard__icon-button"
                aria-label={t("notifications.title")}
                aria-expanded={showNotifications}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <i className="fa-regular fa-bell" />
                {unreadCount > 0 && (
                  <span className="dashboard__icon-badge dashboard__icon-badge--count">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="dashboard__notification-dropdown" role="listbox" aria-label={t("notifications.list")}>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      className="dashboard__notification-view-all"
                      onClick={() => markAllReadMutation.mutate()}
                      disabled={markAllReadMutation.isPending}
                    >
                      {t("notifications.markAllRead")}
                    </button>
                  )}
                  {notificationsLoading ? (
                    <div className="dashboard__notification-item">{t("notifications.loading")}</div>
                  ) : notifications.length === 0 ? (
                    <div className="dashboard__notification-item">{t("notifications.empty")}</div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id ?? notification.createdAt}
                        role="option"
                        className={`dashboard__notification-item ${
                          notification.isRead ? "dashboard__notification-item--read" : ""
                        }`}
                        tabIndex={0}
                        aria-selected={false}
                        onClick={() => handleOpenNotification(notification)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOpenNotification(notification);
                          }
                        }}
                      >
                        <div className="dashboard__notification-title">
                          {notification.title ?? t("notifications.fallbackTitle")}
                        </div>
                        <div className="dashboard__notification-message">{notification.message ?? ""}</div>
                        <div className="dashboard__notification-time">
                          {notification.createdAt ? formatDate(notification.createdAt) : ""}
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    className="dashboard__notification-view-all"
                    onClick={() => {
                      setShowNotifications(false);
                      navigate("/b2b/notifications");
                    }}
                  >
                    {t("notifications.viewAll")}
                  </button>
                </div>
              )}
            </div>
            <LanguageSwitcher />
            <UserMenu />
          </div>
        </header>

        <section className="dashboard__content">{children}</section>
      </main>
    </div>
  );
};

export default DashboardLayout;
